import React, { useMemo } from 'react';
import { useTheme } from 'styled-components';

import Button from 'components/common/Button';
import { ButtonsWrapper } from 'components/common/ButtonsWrapper';
import ErrorMessage from 'components/common/ErrorMessage';
import Label from 'components/common/Label';
import Input from 'components/common/Input';
import Select from 'components/common/Select';
import { Error } from 'components/styled/Typography';

import Nope from 'utils/nope';
import { useForm } from 'utils/hooks';
import { extractErrorMessage } from 'utils/api';
import { supportsSavedView, getViewPresetOptions } from 'utils/bookmarks';
import { useSavedLogViews } from 'services/search';
import { useSensorList } from 'services/sensor';
import type { BookmarkFormValues, BookmarkFormProps } from './types';

export type { BookmarkFormValues } from './types';

const validationSchema = Nope.object().shape({
  name: Nope.string().required('Name is a required field').max(256, 'Name must be shorter than 256 characters'),
  description: Nope.string().max(1024, 'Description must be shorter than 1024 characters'),
  labels: Nope.string().max(1024, 'Labels must be shorter than 1024 characters'),
  query: Nope.string(),
  view: Nope.string(),
});

const handleContainerClick = (e: React.MouseEvent) => {
  e.stopPropagation();
  e.preventDefault();
};

const validate = (values: BookmarkFormValues) => {
  return validationSchema.validate(values);
};

const BookmarkForm = ({
  type,
  error,
  loading,
  bookmark,
  defaultName,
  defaultDescription,
  query,
  searchType,
  onClose,
  onSubmit,
}: BookmarkFormProps) => {
  const theme = useTheme();
  const { list: savedViewsList } = useSavedLogViews();
  const { profilesList } = useSensorList();
  const showViewSelection = type === 'pql' && supportsSavedView(searchType);

  const viewOptions = useMemo(() => {
    if (!showViewSelection || !profilesList?.length) return [];

    const options: { value: string; label: string }[] = [];

    getViewPresetOptions(profilesList).forEach((option) => {
      options.push({ value: option.value, label: option.label });
    });

    if (savedViewsList.data?.items && savedViewsList.data.items.length > 0) {
      savedViewsList.data.items.forEach((view) => {
        if (view.name) {
          options.push({ value: view.name, label: view.name });
        }
      });
    }

    return options;
  }, [showViewSelection, profilesList, savedViewsList.data?.items]);

  const form = useForm<BookmarkFormValues>({
    validate,
    initialValues: {
      name: bookmark.name || defaultName || query || '',
      description: bookmark.description || defaultDescription || '',
      labels: bookmark.labels ? bookmark.labels : '',
      ...(type === 'pql' ? { query: bookmark.query || query } : {}),
      ...(showViewSelection ? { view: bookmark.view || '' } : {}),
    },
    onSubmit,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    form.handleSubmit();
  };

  return (
    <div className="cursor-default" onClick={handleContainerClick}>
      <Label className="mb-1 mt-2" required htmlFor="bookmark-name">
        Name
      </Label>
      <Input
        id="bookmark-name"
        required
        name="name"
        value={form.values.name}
        onChange={form.handleChange}
        data-test-id="add-bookmark-name"
      />
      <ErrorMessage {...form} name="name" />

      <Label className="mb-1 mt-2">Description</Label>
      <Input
        name="description"
        value={form.values.description}
        onChange={form.handleChange}
        data-test-id="add-bookmark-description"
      />
      <ErrorMessage {...form} name="description" />

      <Label className="mb-1 mt-2">Labels</Label>
      <Input name="labels" value={form.values.labels} onChange={form.handleChange} data-test-id="add-bookmark-labels" />
      <ErrorMessage {...form} name="labels" />

      {type === 'pql' && (
        <>
          <Label className="mb-1 mt-2">Query</Label>
          <Input
            name="query"
            value={form.values.query}
            onChange={form.handleChange}
            data-test-id="add-bookmark-query"
          />
          {showViewSelection && (
            <>
              <Label className="mb-1 mt-2">View</Label>
              <Select
                name="view"
                value={viewOptions.find((option) => option.value === form.values.view)}
                onChange={(selectedOption) => {
                  form.handleChange(selectedOption?.value || '', 'view');
                }}
                options={viewOptions}
                minHeight="40px"
                styles={{
                  control: (base) => ({
                    ...base,
                    borderColor: `${theme.colors.border.contrast} !important`,
                  }),
                  option: (base) => ({
                    ...base,
                    fontSize: '14px',
                  }),
                  singleValue: (base) => ({
                    ...base,
                    fontSize: '14px',
                  }),
                }}
              />
            </>
          )}
        </>
      )}
      {error ? <Error>{extractErrorMessage(error as string | null)}</Error> : null}

      <ButtonsWrapper className="mt-6 justify-end mb-4">
        <Button variant="text" onClick={onClose}>
          Cancel
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={handleSubmit}
          isLoading={loading}
          data-test-id="add-bookmark-submit"
        >
          Submit
        </Button>
      </ButtonsWrapper>
    </div>
  );
};

export default React.memo(BookmarkForm);
